import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    computed,
    inject,
    input,
    output,
    signal,
    effect,
    untracked,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, IsActiveMatchOptions } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { OrganizationService } from '../../core/services/organization.service';
import { Theme, THEME_STORAGE_KEY, ThemeService } from '../../core/services/theme.service';
import { AppUrls } from '../../routing/app-urls';
import { environment } from '../../../environments/environment';
import { ApplicationStore, ApplicationStoreType } from '../../core/store/application.store';
import { LayoutStore, LayoutStoreType } from '../../core/store/layout.store';
import { Option } from '../../shared/types/option';
import { OptionToggleComponent } from '../../shared/components/option-toggle/option-toggle.component';
import { ConversationHistoryComponent } from './components/conversation-history/conversation-history.component';
import { GettingStartedComponent } from './components/getting-started/getting-started.component';
import { HotelGptStore, HotelGptStoreType } from '../../core/store/hotel-gpt.store';
import { UserAvatarComponent } from '../../settings/users-access/components/user-avatar/user-avatar.component';
import { ROLE_LABELS } from '../../shared/types/role';
import { SignedInUser } from '../../shared/types/user';
import Intercom, { show as showIntercom } from '@intercom/messenger-js-sdk';
import { OrganizationPrivilege, UserPrivilege } from '../../shared/types/organization';
import { AccessRequestsService } from '../../settings/access-requests/service/access-requests.service';

export enum SidebarView {
    Default,
    Settings,
    Admin,
}

@Component({
    selector: 'juyo-sidebar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        RouterLinkActive,
        NgOptimizedImage,
        OptionToggleComponent,
        ConversationHistoryComponent,
        GettingStartedComponent,
        UserAvatarComponent,
    ],
    styleUrl: 'sidebar.scss',
    host: { '[class.collapsed]': 'collapsed()' },
    template: `
        <div class="branding">
            @if (!collapsed()) {
                <img
                    [ngSrc]="
                        themeService.theme() === Theme.Dark
                            ? 'assets/logos/logo_white.png'
                            : 'assets/logos/logo_dark.png'
                    "
                    width="415"
                    height="129"
                    alt="Juyo"
                    priority
                />
            } @else {
                <img
                    [ngSrc]="
                        themeService.theme() === Theme.Dark
                            ? 'assets/logos/logo_icon_white.png'
                            : 'assets/logos/logo_icon_dark.png'
                    "
                    width="129"
                    height="129"
                    alt="Juyo"
                    priority
                />
            }
        </div>

        @if (!collapsed()) {
            @if (organizationOptions().length > 1) {
                <juyo-option-toggle
                    [options]="organizationOptions()"
                    [selectedOption]="selectedOrganizationOption()"
                    [openOnClick]="true"
                    [displaySearchBar]="true"
                    (optionClick)="onOrganizationSelect($event)"
                />
            } @else if (organizationOptions().length === 1) {
                <span class="chain-name">{{ organizationOptions()[0].label }}</span>
            }
        }

        @switch (view()) {
            @case (SidebarView.Settings) {
                @if (!collapsed()) {
                    <div class="org-profile">
                        <div class="chain-avatar">{{ organizationInitials() }}</div>
                        <span class="org-chain-name">{{ organizationName() }}</span>
                    </div>

                    <button type="button" class="back-btn" (click)="onBackFromSettings()">
                        <span class="material-symbols-outlined">arrow_back</span>
                        Back
                    </button>

                    @if (canAccessOrgSettings()) {
                        <div class="org-nav-section">
                            <span class="org-nav-label">Organization settings</span>
                            <nav class="nav-links">
                                <a
                                    [routerLink]="['/', AppUrls.organizationSettingsUrl]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> settings </span>
                                    Organization settings
                                </a>
                                <a
                                    [routerLink]="[
                                        '/',
                                        AppUrls.organizationSettingsUrl,
                                        AppUrls.organizationSettingsPropertiesPath,
                                    ]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> domain </span>
                                    Properties
                                </a>
                                <a
                                    [routerLink]="[
                                        '/',
                                        AppUrls.organizationSettingsUrl,
                                        AppUrls.organizationSettingsUsersPath,
                                    ]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> group </span>
                                    Users & access
                                </a>
                                <a
                                    [routerLink]="[
                                        '/',
                                        AppUrls.organizationSettingsUrl,
                                        AppUrls.organizationSettingsAccessRequestsPath,
                                    ]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> how_to_reg </span>
                                    Access requests
                                    @if (pendingAccessRequests() > 0) {
                                        <span class="nav-badge" [attr.aria-label]="pendingAccessRequests() + ' pending'">{{
                                            pendingAccessRequests()
                                        }}</span>
                                    }
                                </a>
                                @if (canAccessUsage()) {
                                    <a
                                        [routerLink]="[
                                            '/',
                                            AppUrls.organizationSettingsUrl,
                                            AppUrls.organizationSettingsUsagePath,
                                        ]"
                                        routerLinkActive="active"
                                        [routerLinkActiveOptions]="activeOptions"
                                    >
                                        <span class="material-symbols-outlined"> speed </span>
                                        Usage
                                    </a>
                                }
                            </nav>
                        </div>
                    }

                    <div class="org-nav-section">
                        <span class="org-nav-label">Account settings</span>
                        <nav class="nav-links">
                            <a
                                [routerLink]="['/', AppUrls.accountSettingsUrl, AppUrls.accountSettingsProfilePath]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                            >
                                <span class="material-symbols-outlined"> account_circle </span>
                                Profile
                            </a>
                        </nav>
                    </div>
                } @else {
                    @if (canAccessOrgSettings()) {
                        <nav class="nav-links org-settings-nav">
                            <a
                                [routerLink]="['/', AppUrls.organizationSettingsUrl]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                            >
                                <span class="material-symbols-outlined"> settings </span>
                            </a>
                            <a
                                [routerLink]="[
                                    '/',
                                    AppUrls.organizationSettingsUrl,
                                    AppUrls.organizationSettingsPropertiesPath,
                                ]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                            >
                                <span class="material-symbols-outlined"> domain </span>
                            </a>
                            <a
                                [routerLink]="[
                                    '/',
                                    AppUrls.organizationSettingsUrl,
                                    AppUrls.organizationSettingsUsersPath,
                                ]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                            >
                                <span class="material-symbols-outlined"> group </span>
                            </a>
                            <a
                                class="nav-icon-badge-host"
                                [routerLink]="[
                                    '/',
                                    AppUrls.organizationSettingsUrl,
                                    AppUrls.organizationSettingsAccessRequestsPath,
                                ]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                                [attr.aria-label]="'Access requests' + (pendingAccessRequests() > 0 ? ', ' + pendingAccessRequests() + ' pending' : '')"
                            >
                                <span class="material-symbols-outlined"> how_to_reg </span>
                                @if (pendingAccessRequests() > 0) {
                                    <span class="nav-badge nav-badge--dot">{{ pendingAccessRequests() }}</span>
                                }
                            </a>
                            @if (canAccessUsage()) {
                                <a
                                    [routerLink]="[
                                        '/',
                                        AppUrls.organizationSettingsUrl,
                                        AppUrls.organizationSettingsUsagePath,
                                    ]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> speed </span>
                                </a>
                            }
                        </nav>
                    }
                    <nav class="nav-links org-settings-nav">
                        <a
                            [routerLink]="['/', AppUrls.accountSettingsUrl, AppUrls.accountSettingsProfilePath]"
                            routerLinkActive="active"
                            [routerLinkActiveOptions]="activeOptions"
                        >
                            <span class="material-symbols-outlined"> account_circle </span>
                        </a>
                    </nav>
                }
            }
            @case (SidebarView.Admin) {
                @if (!collapsed()) {
                    <button type="button" class="back-btn" (click)="onBackFromAdmin()">
                        <span class="material-symbols-outlined">arrow_back</span>
                        Back
                    </button>

                    <div class="org-nav-section">
                        <span class="org-nav-label">Juyo Admin Page</span>
                        <nav class="nav-links">
                            @if (canManageBroadcast()) {
                                <a
                                    [routerLink]="['/', AppUrls.adminUrl, AppUrls.adminBroadcastPath]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> campaign </span>
                                    Broadcast
                                </a>
                            }
                            @if (canAccessUsageRates()) {
                                <a
                                    [routerLink]="['/', AppUrls.adminUrl, AppUrls.adminUsageRatesPath]"
                                    routerLinkActive="active"
                                    [routerLinkActiveOptions]="activeOptions"
                                >
                                    <span class="material-symbols-outlined"> speed </span>
                                    Usage rates
                                </a>
                            }
                        </nav>
                    </div>
                } @else {
                    <nav class="nav-links org-settings-nav">
                        @if (canManageBroadcast()) {
                            <a
                                [routerLink]="['/', AppUrls.adminUrl, AppUrls.adminBroadcastPath]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                            >
                                <span class="material-symbols-outlined"> campaign </span>
                            </a>
                        }
                        @if (canAccessUsageRates()) {
                            <a
                                [routerLink]="['/', AppUrls.adminUrl, AppUrls.adminUsageRatesPath]"
                                routerLinkActive="active"
                                [routerLinkActiveOptions]="activeOptions"
                            >
                                <span class="material-symbols-outlined"> speed </span>
                            </a>
                        }
                    </nav>
                }
            }
            @default {
                <button
                    type="button"
                    class="new-chat-btn"
                    (click)="onNewChat()"
                    [class.tour-highlight]="tourStep() === 3"
                >
                    <span class="material-symbols-outlined">add</span>
                    @if (!collapsed()) {
                        New chat
                    }
                </button>
                @if (canAccessDataDrop()) {
                    <nav class="nav-links">
                        <a
                            [routerLink]="['/', AppUrls.dataDropUrl]"
                            routerLinkActive="active"
                            [routerLinkActiveOptions]="activeOptions"
                            [class.tour-highlight]="tourStep() === 2"
                        >
                            <span class="material-symbols-outlined"> download </span>
                            @if (!collapsed()) {
                                Data Drop
                            }
                        </a>
                    </nav>
                }
                <juyo-conversation-history [collapsed]="collapsed()" />
            }
        }

        <juyo-getting-started [collapsed]="collapsed()" />

        <div class="user-footer">
            @if (user(); as u) {
                <juyo-user-avatar
                    [id]="u.id.toString()"
                    [firstName]="u.firstName"
                    [lastName]="u.lastName"
                    [useFixedColor]="true"
                    (click)="showUserMenu.set(!isUserMenuVisible())"
                    size="sm"
                />
            }
            @if (collapsed()) {
                <button type="button" class="footer-icon-btn" (click)="onToggle()">
                    <span class="material-symbols-outlined">chevron_right</span>
                </button>
            } @else {
                @if (trialDaysRemaining() !== null) {
                    <span class="trial-pill-wrapper">
                        <span class="trial-pill">
                            <span class="material-symbols-outlined">timer</span>
                            {{ trialDaysRemaining() }}d
                        </span>
                        <span class="trial-tooltip" role="tooltip">
                            You have {{ trialDaysRemaining() }} days of free usage left. Contact us if you would like to
                            explore additional usage.
                            <a href="mailto:onboarding@juyoanalytics.com">Contact us</a>
                        </span>
                    </span>
                }
                <button type="button" class="footer-icon-btn" (click)="themeService.toggle()">
                    <span class="material-symbols-outlined">{{
                        themeService.theme() === Theme.Dark ? 'light_mode' : 'dark_mode'
                    }}</span>
                </button>
                <button type="button" class="footer-icon-btn" (click)="onToggle()">
                    <span class="material-symbols-outlined">chevron_left</span>
                </button>
                <button type="button" class="footer-icon-btn" (click)="showUserMenu.set(!isUserMenuVisible())">
                    <span class="material-symbols-outlined">more_horiz</span>
                </button>
            }
            @if (isUserMenuVisible()) {
                <div class="user-menu-backdrop" (click)="showUserMenu.set(false)"></div>
                <div class="user-menu">
                    <div class="user-menu-header">
                        @if (user(); as u) {
                            <juyo-user-avatar
                                [id]="u.id.toString()"
                                [firstName]="u.firstName"
                                [lastName]="u.lastName"
                                [useFixedColor]="true"
                                size="sm"
                            />
                            <div class="user-menu-identity">
                                <span class="user-menu-name">{{ u.fullName }}</span>
                                @if (userRole()) {
                                    <span class="user-menu-role-badge">{{ userRole() }}</span>
                                }
                            </div>
                        }
                    </div>
                    <div class="user-menu-divider"></div>
                    @if (canAccessAdmin()) {
                        <button type="button" (click)="onAdminClick()">
                            <span class="material-symbols-outlined">shield_person</span>
                            <span class="label">Juyo Admin Page</span>
                        </button>
                    }
                    @if (canNavigateToAnalytics()) {
                        <button type="button" (click)="onNavigateToAnalytics()">
                            <span class="material-symbols-outlined">open_in_new</span>
                            <span class="label">Back to menu</span>
                        </button>
                    }
                    @if (enableIntercom()) {
                        <button type="button" (click)="openIntercom()">
                            <span class="material-symbols-outlined">forum</span>
                            <span class="label">Ask for help</span>
                        </button>
                    }
                    <button type="button" (click)="onSettingsClick()" [class.tour-highlight]="tourStep() === 1">
                        <span class="material-symbols-outlined">settings</span>
                        <span class="label">Settings</span>
                    </button>
                    <div class="user-menu-divider"></div>
                    <button type="button" class="sign-out-btn" (click)="onSignOutClick()">
                        <span class="material-symbols-outlined">logout</span>
                        <span class="label">Sign out</span>
                    </button>
                </div>
            }
        </div>
    `,
})
export class SidebarComponent {
    private authService = inject(AuthenticationService);
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);
    private organizationService = inject(OrganizationService);
    private hotelGptStore: HotelGptStoreType = inject(HotelGptStore);
    private layoutStore: LayoutStoreType = inject(LayoutStore);
    private accessRequestsService = inject(AccessRequestsService);
    protected applicationStore: ApplicationStoreType = inject(ApplicationStore);
    protected themeService = inject(ThemeService);

    public collapsed = input.required<boolean>();
    public toggled = output<void>();

    protected view = signal<SidebarView>(SidebarView.Default);
    protected showUserMenu = signal(false);
    protected isUserMenuVisible = computed(() => this.showUserMenu() || this.layoutStore.tourStep() === 1);
    protected tourStep = computed(() => this.layoutStore.tourStep());

    protected user = computed<SignedInUser | null>(() => this.applicationStore.signedInUser());

    protected organizationOptions = computed<Option[]>(() => {
        const organizations = this.applicationStore.organizations();
        return organizations.length > 0 ? organizations.map((o) => new Option(o.id, o.name)) : [];
    });

    protected selectedOrganizationOption = computed<Option | undefined>(() => {
        const organization = this.applicationStore.selectedOrganization();
        return organization ? new Option(organization.id, organization.name) : undefined;
    });

    protected organizationInitials = computed(() => {
        const name = this.applicationStore.selectedOrganization()?.name ?? '';
        const words = name.split(' ').filter((w) => w.length > 0);
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return words
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase();
    });

    protected organizationName = computed(() => this.applicationStore.selectedOrganization()?.name ?? '');

    private readonly TRIAL_PERIOD_DAYS = 30;

    protected trialDaysRemaining = computed<number | null>(() => {
        const org = this.applicationStore.selectedOrganization();
        if (!org || org.fromV3) return null;
        const days = this.TRIAL_PERIOD_DAYS - Math.floor((Date.now() - new Date(org.createdAt).getTime()) / 86_400_000);
        return days > 0 ? days : null;
    });

    protected userRole = computed<string | null>(() => {
        const role = this.applicationStore.selectedOrganizationAccess()?.userRole;
        return role ? ROLE_LABELS[role] : null;
    });

    protected canNavigateToAnalytics = computed(() => this.applicationStore.selectedOrganization()?.fromV3);

    protected isJuyoAdmin = computed(() => this.applicationStore.isJuyoUser());

    protected canAccessDataDrop = computed(() => this.applicationStore.hasDataDropAccess());

    protected canAccessOrgSettings = computed(() => {
        return this.applicationStore.hasPrivilege([
            OrganizationPrivilege.OrgManageAllUsersAndProps,
            OrganizationPrivilege.OrgManageUsersAndProps,
            OrganizationPrivilege.OrgManageOwnUsersAndProps,
        ]);
    });

    private pendingCountOrganizationId = computed(() =>
        this.canAccessOrgSettings() ? (this.applicationStore.selectedOrganization()?.id ?? null) : null,
    );

    protected pendingAccessRequests = computed(() =>
        this.canAccessOrgSettings() ? this.accessRequestsService.pendingCount() : 0,
    );

    protected canAccessUsage = computed(() => this.applicationStore.hasPrivilege(OrganizationPrivilege.OrgUsageStats));

    protected canManageBroadcast = computed(() => this.applicationStore.hasPrivilege(UserPrivilege.ManageBroadcast));

    protected canAccessUsageRates = computed(() =>
        false,
        // this.applicationStore.hasPrivilege([UserPrivilege.FullAccessUsageConfig, UserPrivilege.ReadOnlyUsageConfig]),
    );

    protected canAccessAdmin = computed(
        () => this.isJuyoAdmin() && (this.canManageBroadcast() || this.canAccessUsageRates()),
    );

    protected enableIntercom = computed<boolean>(() => {
        const user = this.applicationStore.signedInUser();
        const organization = this.applicationStore.selectedOrganization();

        return !!user && !!organization?.fromV3;
    });

    protected readonly externalPlatformUrl = environment.externalPlatformUrl;
    protected readonly AppUrls = AppUrls;
    protected readonly Theme = Theme;
    protected readonly SidebarView = SidebarView;

    protected readonly activeOptions: IsActiveMatchOptions = {
        paths: 'exact',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
    };

    constructor() {
        const viewForUrl = (url: string): SidebarView => {
            if (url.startsWith(`/${AppUrls.adminUrl}`)) {
                return SidebarView.Admin;
            }
            if (
                url.startsWith(`/${AppUrls.organizationSettingsUrl}`) ||
                url.startsWith(`/${AppUrls.accountSettingsUrl}`)
            ) {
                return SidebarView.Settings;
            }
            return SidebarView.Default;
        };

        this.view.set(viewForUrl(this.router.url));

        effect(() => {
            const organizationId = this.pendingCountOrganizationId();
            if (organizationId) {
                untracked(() => this.accessRequestsService.refreshPendingCount(organizationId));
            }
        });

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((e) => {
                this.view.set(viewForUrl((e as NavigationEnd).url));
            });

        const initIntercomEffect = effect(() => {
            const user = this.applicationStore.signedInUser();
            const enableIntercom = this.enableIntercom();

            if (user && enableIntercom) {
                Intercom({
                    app_id: 'su9k72tw',
                    region: 'eu',
                    user_id: user.id.toString(),
                    name: user.fullName,
                    email: user.email,
                    hide_default_launcher: true,
                    hide_notifications: true,
                });
                initIntercomEffect.destroy();
            }
        });
    }

    protected onToggle(): void {
        this.showUserMenu.set(false);
        this.toggled.emit();
    }

    protected onBackFromSettings(): void {
        this.router.navigate(['/', AppUrls.hotelGptUrl]);
    }

    protected onBackFromAdmin(): void {
        this.router.navigate(['/', AppUrls.hotelGptUrl]);
    }

    protected onAdminClick(): void {
        const adminPath = this.canManageBroadcast() ? AppUrls.adminBroadcastPath : AppUrls.adminUsageRatesPath;
        this.router.navigate(['/', AppUrls.adminUrl, adminPath]);
        this.showUserMenu.set(false);
    }

    protected onNewChat(): void {
        this.hotelGptStore.startNewChat();
        this.router.navigate(['/', AppUrls.hotelGptUrl]);
    }

    protected onOrganizationSelect(option: Option): void {
        const organization = this.applicationStore.organizations().find((o) => o.id === option.key);
        if (organization) {
            this.organizationService.selectOrganization(organization);
        }
    }

    protected onNavigateToAnalytics(): void {
        if (this.canNavigateToAnalytics()) {
            const tokens = { ...this.authService.getTokensForExport(), [THEME_STORAGE_KEY]: this.themeService.theme() };
            const encoded = btoa(JSON.stringify(tokens));
            const selectedOrganization = this.applicationStore.selectedOrganization();
            const chainParam = selectedOrganization ? `?chainId=${selectedOrganization.chainId}` : '';
            window.location.href = `${this.externalPlatformUrl}${chainParam}#tokens=${encoded}`;
        }
    }

    protected onSettingsClick(): void {
        if (this.canAccessOrgSettings()) {
            this.router.navigate(['/', AppUrls.organizationSettingsUrl]);
        } else {
            this.router.navigate(['/', AppUrls.accountSettingsUrl, AppUrls.accountSettingsProfilePath]);
        }
        this.showUserMenu.set(false);
    }

    protected openIntercom(): void {
        showIntercom();
    }

    protected async onSignOutClick(): Promise<void> {
        await this.authService.signOut();
        await this.router.navigate([AppUrls.signInUrl]);
        this.showUserMenu.set(false);
    }
}
